import React, { useState } from 'react';
import { X, Upload, Loader2, Camera, Eye, Download, Share2, Sparkles, ZoomIn, Maximize2, Save, ShoppingBag } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VirtualTryOnModal = ({ isOpen, onClose, product, storyId = null, onGarantirPeca = null }) => {
  // Estado local
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showImageZoom, setShowImageZoom] = useState(false); // Modal de zoom
  const [zoomImage, setZoomImage] = useState(null); // Imagem para zoom
  const [zoomImageIndex, setZoomImageIndex] = useState(0); // Índice da imagem no zoom (para carrossel)
  const [zoomImages, setZoomImages] = useState([]); // Array de imagens para carrossel no zoom
  const [credits, setCredits] = useState(null); // Sistema de créditos
  const [selectedSize, setSelectedSize] = useState(''); // Tamanho selecionado
  
  // Session ID para controle de créditos
  const [sessionId] = useState(() => {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  });

  // Cache de resultados por produto usando sessionStorage para segurança
  // Cada sessão (aba do navegador) tem seu próprio cache isolado
  const getCachedResult = (productId) => {
    if (!productId) return null;
    try {
      const sessionId = getSessionId();
      const cacheKey = `tryon_result_${sessionId}_${productId}`;
      const cached = sessionStorage.getItem(cacheKey);
      
      if (cached) {
        const data = JSON.parse(cached);
        return data.result;
      }
      return null;
    } catch (e) {
      console.error('Erro ao carregar resultado do cache:', e);
      return null;
    }
  };

  const setCachedResult = (productId, resultUrl) => {
    if (!productId || !resultUrl) return;
    try {
      const sessionId = getSessionId();
      const cacheKey = `tryon_result_${sessionId}_${productId}`;
      
      sessionStorage.setItem(cacheKey, JSON.stringify({
        result: resultUrl,
        timestamp: Date.now()
      }));
      
      console.log(`✅ Resultado salvo no cache da sessão ${sessionId} para produto ${productId}`);
    } catch (e) {
      console.error('Erro ao salvar resultado no cache:', e);
    }
  };

  const clearCachedResult = (productId) => {
    if (!productId) return;
    try {
      const sessionId = getSessionId();
      const cacheKey = `tryon_result_${sessionId}_${productId}`;
      sessionStorage.removeItem(cacheKey);
      console.log(`🗑️ Resultado deletado do cache para produto ${productId}`);
    } catch (e) {
      console.error('Erro ao deletar resultado do cache:', e);
    }
  };

  const clearAllCachedResults = () => {
    try {
      // Limpar todos os resultados salvos da sessão atual
      const sessionId = getSessionId();
      const keys = Object.keys(sessionStorage);
      keys.forEach(key => {
        if (key.startsWith(`tryon_result_${sessionId}_`)) {
          sessionStorage.removeItem(key);
        }
      });
      console.log(`🗑️ Todos os resultados da sessão ${sessionId} deletados do cache`);
    } catch (e) {
      console.error('Erro ao limpar todos os resultados:', e);
    }
  };

  // ========== CORREÇÃO DE SEGURANÇA ==========
  // Cache da foto do cliente usando sessionStorage + ID único da sessão
  // Isso garante que cada pessoa que acessa o link tenha seu próprio cache isolado
  
  // Gerar ID único para esta sessão (não muda até fechar aba)
  const getSessionId = () => {
    let sessionId = sessionStorage.getItem('tryon_session_id');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('tryon_session_id', sessionId);
    }
    return sessionId;
  };

  const getCachedPhoto = () => {
    try {
      const sessionId = getSessionId();
      const cacheKey = `tryon_photo_${sessionId}`;
      const cached = sessionStorage.getItem(cacheKey);
      
      if (cached) {
        const data = JSON.parse(cached);
        // Verificar se não é muito antiga (1 hora)
        if (Date.now() - data.timestamp < 3600000) {
          return data.preview;
        } else {
          // Limpar cache expirado
          sessionStorage.removeItem(cacheKey);
        }
      }
      return null;
    } catch (e) {
      console.error('Erro ao recuperar foto do cache:', e);
      return null;
    }
  };

  const setCachedPhoto = (preview) => {
    try {
      const sessionId = getSessionId();
      const cacheKey = `tryon_photo_${sessionId}`;
      
      sessionStorage.setItem(cacheKey, JSON.stringify({
        preview: preview,
        timestamp: Date.now()
      }));
      
      console.log(`✅ Foto salva no cache da sessão: ${sessionId}`);
    } catch (e) {
      console.error('Erro ao salvar foto no cache:', e);
    }
  };

  const clearCachedPhoto = () => {
    try {
      const sessionId = getSessionId();
      const cacheKey = `tryon_photo_${sessionId}`;
      sessionStorage.removeItem(cacheKey);
      console.log(`🗑️ Cache da foto limpo para sessão: ${sessionId}`);
    } catch (e) {
      console.error('Erro ao limpar cache:', e);
    }
  };

  // Inicializar com foto em cache (apenas preview)
  const [customerPhoto, setCustomerPhoto] = useState(null);

  const [customerPhotoPreview, setCustomerPhotoPreview] = useState(() => {
    return getCachedPhoto();
  });

  // Carregar créditos e resultado salvo quando o produto muda
  React.useEffect(() => {
    if (product?.id && isOpen) {
      // Carregar resultado do cache
      const cachedResult = getCachedResult(product.id);
      if (cachedResult) {
        setResult(cachedResult);
        console.log(`✅ Resultado carregado do cache para produto ${product.id}`);
      }
      
      // Carregar créditos disponíveis
      if (storyId) {
        loadCredits();
      }
      
      // NÃO selecionar tamanho automaticamente - cliente DEVE escolher
      // if (product.sizes && product.sizes.length > 0) {
      //   setSelectedSize(product.sizes[0]);
      // }
    }
  }, [product?.id, isOpen, storyId]);
  
  const loadCredits = async () => {
    try {
      const response = await axios.get(
        `${API}/products/${product.id}/modal?session_id=${sessionId}&story_id=${storyId}`
      );
      if (response.data.credits) {
        setCredits(response.data.credits);
      }
    } catch (error) {
      console.error('Erro ao carregar créditos:', error);
    }
  };

  // Verificar se tem resultado em cache (memoizado)
  const [hasCachedResult, setHasCachedResult] = React.useState(false);
  
  React.useEffect(() => {
    if (product?.id && isOpen) {
      setHasCachedResult(!!getCachedResult(product.id));
    } else {
      setHasCachedResult(false);
    }
  }, [product?.id, isOpen]);

  const enhanceImageForTryOn = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      let objectUrl = null;
      
      img.onload = () => {
        try {
          // Calcular novo tamanho - otimizado para FASHN AI v1.6 (até 864x1296)
          let { width, height } = img;
          const maxWidth = 864;
          const maxHeight = 1296;
          
          // Manter aspect ratio e otimizar para melhor processamento
          if (width > height) {
            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }
          } else {
            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }
          }
          
          // Configurar canvas
          canvas.width = width;
          canvas.height = height;
          
          // OTIMIZAÇÕES para melhor detecção de mãos/corpo:
          
          // 1. Suavização para reduzir ruído (melhor detecção de contornos)
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          
          // 2. Desenhar imagem base
          ctx.drawImage(img, 0, 0, width, height);
          
          // 3. Aplicar filtros para melhorar contraste e definição
          const imageData = ctx.getImageData(0, 0, width, height);
          const data = imageData.data;
          
          // Filtro de contraste e nitidez otimizado para detecção corporal
          for (let i = 0; i < data.length; i += 4) {
            // Aumentar contraste suavemente (10%)
            data[i] = Math.min(255, data[i] * 1.1);     // R
            data[i + 1] = Math.min(255, data[i + 1] * 1.1); // G
            data[i + 2] = Math.min(255, data[i + 2] * 1.1); // B
            
            // Melhorar saturação das cores da pele
            const avg = (data[i] + data[i + 1] + data[i + 2]) / 3;
            const factor = 1.05; // Leve aumento de saturação
            data[i] = Math.min(255, avg + (data[i] - avg) * factor);
            data[i + 1] = Math.min(255, avg + (data[i + 1] - avg) * factor);
            data[i + 2] = Math.min(255, avg + (data[i + 2] - avg) * factor);
          }
          
          ctx.putImageData(imageData, 0, 0);
          
          // Converter para blob com alta qualidade (95%)
          canvas.toBlob((blob) => {
            // Limpar URL criada para liberar memória
            if (objectUrl) {
              URL.revokeObjectURL(objectUrl);
            }
            
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Erro ao processar imagem'));
            }
          }, 'image/jpeg', 0.95);
        } catch (error) {
          // Limpar URL em caso de erro
          if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
          }
          reject(error);
        }
      };
      
      img.onerror = () => {
        // Limpar URL em caso de erro
        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
        reject(new Error('Erro ao carregar imagem'));
      };
      
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo de arquivo
      if (file.type && !file.type.startsWith('image/')) {
        setError('Por favor, selecione apenas arquivos de imagem');
        return;
      }
      
      // Validar tamanho (máx 10MB)
      if (file.size > 10 * 1024 * 1024) {
        setError('Imagem muito grande. Tamanho máximo: 10MB');
        return;
      }
      
      try {
        // OTIMIZAR imagem para melhor qualidade de mãos/corpo
        const enhancedBlob = await enhanceImageForTryOn(file);
        const enhancedFile = new File([enhancedBlob], file.name, { type: 'image/jpeg' });
        
        console.log(`📸 Imagem otimizada: ${file.size} → ${enhancedFile.size} bytes`);
        
        setCustomerPhoto(enhancedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          const preview = reader.result;
          setCustomerPhotoPreview(preview);
          // Salvar apenas o preview no cache (não o File)
          setCachedPhoto(preview);
        };
        reader.onerror = () => {
          setError('Erro ao ler a imagem. Tente novamente.');
        };
        reader.readAsDataURL(enhancedFile);
        setError(null);
        setResult(null);
      } catch (err) {
        console.error('Erro ao otimizar imagem:', err);
        setError('Erro ao otimizar imagem: ' + err.message);
      }
    }
  };

  const handleGenerate = async () => {
    if ((!customerPhoto && !customerPhotoPreview) || !product) {
      setError('Por favor, envie sua foto');
      return;
    }

    // Verificar créditos se estiver vindo do Instagram Stories
    if (storyId && credits) {
      if (!credits.has_credits) {
        setError('Você já usou suas 3 gerações gratuitas! Entre em contato via WhatsApp para mais.');
        // Abrir WhatsApp
        const whatsappMsg = encodeURIComponent('Olá! Gostei muito das gerações e gostaria de mais créditos. Pode me ajudar?');
        window.open(`https://wa.me/5511999999999?text=${whatsappMsg}`, '_blank');
        return;
      }

      // Usar crédito antes de gerar
      try {
        const creditResponse = await axios.post(`${API}/products/${product.id}/use-credit`, {
          session_id: sessionId,
          story_id: storyId
        });

        if (!creditResponse.data.success) {
          setError(creditResponse.data.message);
          return;
        }

        // Atualizar créditos
        setCredits(creditResponse.data.credits);
      } catch (error) {
        setError('Erro ao verificar créditos. Tente novamente.');
        return;
      }
    }

    setGenerating(true);
    setError(null);
    setResult(null); // Limpar resultado anterior

    try {
      // Preparar arquivo da foto do cliente
      let photoFile = customerPhoto;
      
      // Se não temos File mas temos preview (veio do cache), converter base64 para File
      if (!photoFile && customerPhotoPreview) {
        const response = await fetch(customerPhotoPreview);
        const blob = await response.blob();
        photoFile = new File([blob], 'customer-photo.jpg', { type: 'image/jpeg' });
        setCustomerPhoto(photoFile); // Atualizar estado para próximas gerações
      }

      const formData = new FormData();
      formData.append('model_image', photoFile, photoFile.name);
      
      // 🆕 Reunir TODAS as imagens do produto para melhor qualidade
      const allProductImages = [
        ...(product.image_urls?.filter(url => url?.trim()) || []),
        ...(product.photos?.map(photo => photo.url) || []),
        product.image,
        product.images?.[0]
      ].filter(Boolean);
      
      if (allProductImages.length === 0) {
        throw new Error('Produto sem imagem disponível');
      }

      console.log(`📸 Enviando ${allProductImages.length} imagens do produto para melhor qualidade`);

      // 🆕 Enviar array de URLs (backend vai tentar todas)
      formData.append('product_image_urls', JSON.stringify(allProductImages));
      formData.append('category', 'auto'); // FASHN AI detecta automaticamente
      formData.append('num_samples', '1');

      console.log('🚀 Enviando requisição para API (modo qualidade)...');

      const response = await axios.post(`${API}/virtual-tryon`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 segundos (modo quality ~19s + processamento)
      });

      if (response.data.success && response.data.images?.length > 0) {
        const resultUrl = response.data.images[0];
        setResult(resultUrl);
        
        // Salvar resultado no cache para persistir
        if (product?.id) {
          setCachedResult(product.id, resultUrl);
        }
      } else {
        throw new Error('Nenhuma imagem foi gerada pela IA');
      }
    } catch (err) {
      console.error('❌ Erro completo:', err);
      
      let errorMessage = 'Erro ao processar. Tente novamente.';
      
      if (err.code === 'ECONNABORTED') {
        errorMessage = 'Tempo limite excedido. A IA está demorando muito. Tente novamente.';
      } else if (err.response) {
        // Erro da API
        const detail = err.response.data?.detail || err.response.data?.message || errorMessage;
        
        // Detectar erro de pose e mostrar mensagem amigável
        if (detail.includes('PoseError') || detail.includes('pose') || detail.includes('body pose')) {
          errorMessage = '⚠️ Não foi possível detectar a pose corporal na foto.\n\n' +
                        '📸 Para melhores resultados, use uma foto com:\n' +
                        '✅ Pessoa de pé, de frente\n' +
                        '✅ Corpo inteiro ou da cintura para cima\n' +
                        '✅ Braços visíveis\n' +
                        '✅ Boa iluminação\n' +
                        '✅ Fundo simples\n\n' +
                        '❌ Evite: selfies, fotos cortadas, poses sentadas';
        } else {
          errorMessage = detail;
        }
      } else if (err.request) {
        // Sem resposta do servidor
        errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão.';
      } else {
        // Outros erros
        errorMessage = err.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = async () => {
    if (!result) return;
    
    try {
      // Buscar imagem como blob
      const response = await fetch(result);
      const blob = await response.blob();
      
      // Criar URL temporária
      const url = URL.createObjectURL(blob);
      
      // Criar link de download gerenciado por React
      const link = document.createElement('a');
      link.href = url;
      link.download = `try-on-${product?.product || product?.name || 'produto'}.jpg`;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup: remover link e revogar URL após uso
      setTimeout(() => {
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Erro ao fazer download:', error);
      setError('Erro ao fazer download da imagem');
    }
  };

  const handleScreenshot = async () => {
    if (!result) return;
    
    try {
      // Para mobile - usar API moderna de compartilhamento
      if (navigator.share && /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)) {
        // Converter base64 para blob
        const response = await fetch(result);
        const blob = await response.blob();
        const file = new File([blob], `try-on-${Date.now()}.jpg`, { type: 'image/jpeg' });
        
        await navigator.share({
          title: 'Minha Prova Virtual',
          text: `Veja como ficou ${product?.product || 'este produto'} em mim!`,
          files: [file]
        });
      } else {
        // Desktop - download direto
        handleDownload();
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
      // Fallback para download
      handleDownload();
    }
  };

  const openImageZoom = (imageUrl, title, allImages = null, startIndex = 0) => {
    setZoomImage({ url: imageUrl, title });
    if (allImages && allImages.length > 1) {
      setZoomImages(allImages);
      setZoomImageIndex(startIndex);
    } else {
      setZoomImages([]);
      setZoomImageIndex(0);
    }
    setShowImageZoom(true);
  };

  const closeImageZoom = () => {
    setShowImageZoom(false);
    setZoomImage(null);
    setZoomImages([]);
    setZoomImageIndex(0);
  };
  
  const navigateZoomImage = (direction) => {
    if (zoomImages.length <= 1) return;
    
    const newIndex = direction === 'next'
      ? (zoomImageIndex + 1) % zoomImages.length
      : (zoomImageIndex - 1 + zoomImages.length) % zoomImages.length;
    
    setZoomImageIndex(newIndex);
    setZoomImage({ url: zoomImages[newIndex], title: zoomImage.title });
  };

  const handleReset = () => {
    // Apenas limpar resultado e erro, manter a foto
    setResult(null);
    setError(null);
  };

  const handleChangePhoto = () => {
    // Trocar foto completamente (limpar tudo)
    setCustomerPhoto(null);
    setCustomerPhotoPreview(null);
    setResult(null);
    setError(null);
    clearCachedPhoto();
  };

  if (!isOpen) return null;

  return (
    <div key={`tryon-modal-${product?.id}`} className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-2 sm:p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Header - Otimizado para Mobile */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white p-3 sm:p-6 flex items-center justify-between z-10">
          <div className="flex-1 min-w-0">
            <h2 className="text-lg sm:text-2xl font-bold flex items-center gap-2">
              <Eye className="w-5 h-5 sm:w-7 sm:h-7 flex-shrink-0" />
              <span className="truncate">Ver em Mim</span>
            </h2>
            <p className="text-xs sm:text-sm text-purple-100 mt-1 truncate">
              Veja como {product?.name} fica em você
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors flex-shrink-0 ml-2"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        </div>

        {/* Content - Padding reduzido para mobile */}
        <div className="p-3 sm:p-6">
          {!result ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
              {/* Upload Section */}
              <div className="space-y-3 sm:space-y-4">
                <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-4 sm:p-6 border-2 border-purple-200">
                  <h3 className="font-bold text-gray-800 mb-2 sm:mb-3 flex items-center gap-2 text-sm sm:text-base">
                    <Camera className="w-4 h-4 sm:w-5 sm:h-5 text-purple-600" />
                    Sua Foto
                  </h3>
                  
                  {!customerPhotoPreview ? (
                    <div className="space-y-3">
                      {/* Dica Rápida */}
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-xs text-blue-800 font-semibold mb-1">💡 Dica para melhores resultados:</p>
                        <p className="text-xs text-blue-700">
                          Fique de pé, braços <strong>relaxados ao lado do corpo</strong>, mãos abertas e visíveis
                        </p>
                      </div>
                      
                      {/* Botão Tirar Foto (Câmera) */}
                      <label className="block cursor-pointer">
                        <div className="border-2 border-purple-400 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg p-6 text-center hover:from-purple-600 hover:to-pink-600 transition-colors">
                          <Camera className="w-10 h-10 text-white mx-auto mb-2" />
                          <p className="text-white font-bold mb-1">
                            📸 Tirar Foto Agora
                          </p>
                          <p className="text-xs text-purple-100">
                            Use a câmera do seu celular
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          capture="user"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </label>

                      {/* Divisor */}
                      <div className="flex items-center gap-3">
                        <div className="flex-1 border-t border-purple-300"></div>
                        <span className="text-xs text-gray-500 font-medium">OU</span>
                        <div className="flex-1 border-t border-purple-300"></div>
                      </div>

                      {/* Botão Escolher da Galeria */}
                      <label className="block cursor-pointer">
                        <div className="border-2 border-dashed border-purple-300 rounded-lg p-6 text-center hover:border-purple-500 hover:bg-purple-50 transition-colors bg-white">
                          <Upload className="w-10 h-10 text-purple-400 mx-auto mb-2" />
                          <p className="text-gray-700 font-bold mb-1">
                            🖼️ Escolher da Galeria
                          </p>
                          <p className="text-xs text-gray-500">
                            PNG, JPG ou JPEG (máx. 10MB)
                          </p>
                        </div>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handlePhotoUpload}
                          className="hidden"
                        />
                      </label>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      <img
                        src={customerPhotoPreview}
                        alt="Sua foto"
                        className="w-full h-48 sm:h-64 object-contain rounded-lg border-2 border-purple-300 bg-gray-50"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={handleChangePhoto}
                          className="flex-1 py-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
                        >
                          Trocar foto
                        </button>
                        {getCachedPhoto() && (
                          <button
                            onClick={clearCachedPhoto}
                            className="flex-1 py-2 text-sm text-gray-600 hover:text-gray-700 font-medium"
                          >
                            Limpar cache
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Product Section - COM CARROSSEL */}
              <div className="space-y-4">
                <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                  <h3 className="font-bold text-gray-800 mb-3">Produto Selecionado</h3>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    {/* Carrossel de Imagens do Produto */}
                    <div className="relative group mb-3">
                      <img
                        src={(() => {
                          // Pegar todas as imagens do produto
                          const allImages = [
                            ...(product?.image_urls?.filter(url => url?.trim()) || []),
                            ...(product?.photos?.map(photo => photo.url) || []),
                            product?.image,
                            product?.images?.[0]
                          ].filter(Boolean);
                          
                          // Se tiver ProductImageIndex definido, usar ele
                          const currentIndex = window.productImageIndex || 0;
                          return allImages[currentIndex] || allImages[0] || 'https://via.placeholder.com/300x300?text=Sem+Imagem';
                        })()}
                        alt={product?.product || product?.name}
                        className="w-full h-48 sm:h-64 object-contain rounded-lg bg-gray-50 cursor-pointer transition-transform hover:scale-105"
                        onClick={() => {
                          const allImages = [
                            ...(product?.image_urls?.filter(url => url?.trim()) || []),
                            ...(product?.photos?.map(photo => photo.url) || []),
                            product?.image,
                            product?.images?.[0]
                          ].filter(Boolean);
                          const currentIndex = window.productImageIndex || 0;
                          openImageZoom(
                            allImages[currentIndex] || allImages[0], 
                            product?.product || product?.name,
                            allImages,
                            currentIndex
                          );
                        }}
                      />
                      
                      {/* Setas de Navegação - Aparecem se tiver mais de 1 imagem */}
                      {(() => {
                        const allImages = [
                          ...(product?.image_urls?.filter(url => url?.trim()) || []),
                          ...(product?.photos?.map(photo => photo.url) || []),
                          product?.image,
                          product?.images?.[0]
                        ].filter(Boolean);
                        
                        if (allImages.length > 1) {
                          // Inicializar índice se não existe
                          if (typeof window.productImageIndex === 'undefined') {
                            window.productImageIndex = 0;
                          }
                          
                          return (
                            <>
                              {/* Seta Esquerda */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.productImageIndex = (window.productImageIndex - 1 + allImages.length) % allImages.length;
                                  // Force re-render
                                  const img = e.target.closest('.relative').querySelector('img');
                                  img.src = allImages[window.productImageIndex];
                                }}
                                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                              </button>
                              
                              {/* Seta Direita */}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  window.productImageIndex = (window.productImageIndex + 1) % allImages.length;
                                  // Force re-render
                                  const img = e.target.closest('.relative').querySelector('img');
                                  img.src = allImages[window.productImageIndex];
                                }}
                                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 hover:bg-opacity-75 text-white p-2 rounded-full transition-all opacity-0 group-hover:opacity-100"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </button>
                              
                              {/* Indicadores de Posição */}
                              <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
                                {allImages.map((_, idx) => (
                                  <div
                                    key={idx}
                                    className={`w-2 h-2 rounded-full transition-all ${
                                      idx === (window.productImageIndex || 0) ? 'bg-white w-4' : 'bg-white bg-opacity-50'
                                    }`}
                                  />
                                ))}
                              </div>
                            </>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Ícone de Zoom no Hover */}
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 pointer-events-none">
                        <ZoomIn className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    
                    <h4 className="font-bold text-gray-900">{product?.product || product?.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{product?.description}</p>
                    <p className="text-lg font-bold text-purple-600 mt-2">
                      R$ {product?.price?.toFixed(2)}
                    </p>
                    
                    {/* Seletor de Tamanhos - COMPACTO */}
                    {product?.sizes && product.sizes.length > 0 && onGarantirPeca && (
                      <div className="mt-3">
                        <label className="block text-xs font-medium text-gray-600 mb-1.5">
                          {selectedSize ? (
                            <span className="text-green-600">✓ Tamanho: {selectedSize}</span>
                          ) : (
                            <span className="text-gray-700">Escolha o tamanho:</span>
                          )}
                        </label>
                        <div className="flex gap-1.5 flex-wrap">
                          {product.sizes.map((size) => (
                            <button
                              key={size}
                              onClick={() => setSelectedSize(size)}
                              className={`px-3 py-1.5 rounded text-xs font-bold transition-all ${
                                selectedSize === size
                                  ? 'bg-purple-600 text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                              }`}
                            >
                              {size}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Botão Garantir Peça Agora - Pequeno e à direita */}
                    {onGarantirPeca && (
                      <div className="mt-3 flex justify-end">
                        <button
                          onClick={() => {
                            if (product.sizes && product.sizes.length > 0 && !selectedSize) {
                              alert('⚠️ Por favor, selecione o tamanho antes de garantir a peça');
                              return;
                            }
                            onGarantirPeca({ product, size: selectedSize });
                          }}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-bold rounded-lg transition-colors flex items-center gap-2"
                        >
                          <ShoppingBag className="w-4 h-4" />
                          Garantir Peça Agora
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Cache Info */}
                {getCachedPhoto() && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                    <p className="text-xs text-green-800 font-medium">
                      ✅ Foto salva! Será reutilizada em outros produtos
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            /* Result Display */
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-xl p-6 border-2 border-green-200">
                <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2">
                  <Eye className="w-6 h-6 text-green-600" />
                  Resultado - Veja como ficou!
                </h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">Foto Original</p>
                    <div className="relative group">
                      <img
                        src={customerPhotoPreview}
                        alt="Original"
                        className="w-full h-64 sm:h-96 object-contain rounded-lg border-2 border-gray-300 cursor-pointer transition-transform hover:scale-105"
                        onClick={() => openImageZoom(customerPhotoPreview, 'Sua Foto Original')}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <ZoomIn className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-2 font-medium">Com o Produto</p>
                    <div className="relative group">
                      <img
                        src={result}
                        alt="Resultado"
                        className="w-full h-64 sm:h-96 object-contain rounded-lg border-2 border-green-400 shadow-lg cursor-pointer transition-transform hover:scale-105"
                        onClick={() => openImageZoom(result, 'Resultado com o Produto')}
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-opacity rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100">
                        <Maximize2 className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-3 mt-4">
                {/* Créditos Restantes */}
                {storyId && credits && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                    <span className="text-sm font-medium text-purple-800">
                      Gerações Restantes: <span className="font-bold text-lg">{credits.remaining}/{credits.total}</span>
                    </span>
                  </div>
                )}
                
                {/* Botões de Ação */}
                <div className="flex gap-3">
                  <button
                    onClick={handleDownload}
                    className="flex-1 py-3 bg-gray-600 text-white rounded-lg font-bold hover:bg-gray-700 transition flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    Download
                  </button>
                  
                  {/* Botão Garantir Peça (se tiver callback) */}
                  {onGarantirPeca && (
                    <button
                      onClick={() => {
                        // VALIDAÇÃO OBRIGATÓRIA DE TAMANHO
                        if (product.sizes && product.sizes.length > 0 && !selectedSize) {
                          alert('⚠️ Por favor, selecione o tamanho antes de garantir a peça');
                          // Scroll para a seção de tamanhos
                          const sizeSection = document.querySelector('[class*="Tamanho"]');
                          if (sizeSection) {
                            sizeSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          }
                          return;
                        }
                        onGarantirPeca({ product, size: selectedSize });
                      }}
                      className="flex-1 py-3 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition flex items-center justify-center gap-2"
                    >
                      ✅ Garantir Peça Agora
                    </button>
                  )}
                </div>
                
                {/* Linha 4: Gerar Novamente e Trocar Foto */}
                <div className="flex gap-3">
                  <button
                    onClick={handleReset}
                    className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold hover:from-purple-700 hover:to-pink-700 transition-colors flex items-center justify-center gap-2"
                  >
                    <Sparkles className="w-5 h-5" />
                    Gerar Novamente
                  </button>
                  <button
                    onClick={handleChangePhoto}
                    className="flex-1 py-3 bg-gray-200 text-gray-700 rounded-lg font-bold hover:bg-gray-300 transition-colors flex items-center justify-center gap-2"
                  >
                    <Camera className="w-5 h-5" />
                    Trocar Foto
                  </button>
                </div>

                {/* Linha 3: Deletar Resultado Salvo (apenas se tiver cache) */}
                {hasCachedResult && (
                  <button
                    onClick={() => {
                      if (window.confirm('🗑️ Deletar este resultado salvo? Esta ação não pode ser desfeita.')) {
                        clearCachedResult(product.id);
                        setResult(null);
                        alert('✅ Resultado deletado! A próxima vez será gerada uma nova imagem.');
                      }
                    }}
                    className="w-full py-2 bg-red-100 text-red-700 rounded-lg font-medium hover:bg-red-200 transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    <X className="w-4 h-4" />
                    Deletar Resultado Salvo
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-700 font-medium">❌ {error}</p>
            </div>
          )}

          {/* Generate Button */}
          {!result && customerPhotoPreview && (
            <button
              onClick={handleGenerate}
              disabled={generating || !customerPhoto}
              className="w-full mt-4 sm:mt-6 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-bold text-base sm:text-lg hover:from-purple-700 hover:to-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 sm:gap-3"
            >
              {generating ? (
                <>
                  <Loader2 className="w-6 h-6 animate-spin" />
                  Processando em Qualidade Máxima... (~19s)
                </>
              ) : (
                <>
                  <Sparkles className="w-6 h-6" />
                  Gerar com IA Qualidade Máxima (~19s)
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* Modal de Zoom da Imagem */}
      {showImageZoom && zoomImage && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black bg-opacity-95 p-2 sm:p-4">
          <div className="relative max-w-[98vw] sm:max-w-[95vw] max-h-[98vh] sm:max-h-[95vh] flex flex-col">
            {/* Header do Zoom - Compacto para mobile */}
            <div className="flex items-center justify-between bg-black bg-opacity-50 text-white p-2 sm:p-3 rounded-t-lg">
              <h3 className="font-bold text-sm sm:text-lg truncate flex-1 mr-2">{zoomImage.title}</h3>
              <button
                onClick={closeImageZoom}
                className="p-1.5 sm:p-2 hover:bg-white/20 rounded-full transition-colors flex-shrink-0"
              >
                <X className="w-5 h-5 sm:w-6 sm:h-6" />
              </button>
            </div>
            
            {/* Imagem Ampliada com Carrossel - Otimizado para mobile */}
            <div className="flex-1 overflow-hidden rounded-b-lg bg-white relative">
              <img
                src={zoomImage.url}
                alt={zoomImage.title}
                className="w-full h-full object-contain max-h-[75vh] sm:max-h-[80vh]"
                onClick={(e) => e.stopPropagation()}
              />
              
              {/* Setas de Navegação no Zoom - Menores em mobile */}
              {zoomImages.length > 1 && (
                <>
                  {/* Seta Esquerda */}
                  <button
                    onClick={() => navigateZoomImage('prev')}
                    className="absolute left-1 sm:left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-2 sm:p-4 rounded-full transition-all shadow-lg"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  
                  {/* Seta Direita */}
                  <button
                    onClick={() => navigateZoomImage('next')}
                    className="absolute right-1 sm:right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-70 hover:bg-opacity-90 text-white p-2 sm:p-4 rounded-full transition-all shadow-lg"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 sm:w-6 sm:h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                  
                  {/* Contador de Imagens */}
                  <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-70 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-full text-xs sm:text-sm font-medium">
                    {zoomImageIndex + 1} / {zoomImages.length}
                  </div>
                </>
              )}
            </div>
            
            {/* Botões de Ação no Zoom */}
            {zoomImage.url === result && (
              <div className="absolute bottom-4 right-4 flex gap-2">
                <button
                  onClick={handleScreenshot}
                  className="p-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors shadow-lg"
                  title={/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ? 'Salvar/Compartilhar' : 'Salvar Imagem'}
                >
                  <Save className="w-5 h-5" />
                </button>
                <button
                  onClick={handleDownload}
                  className="p-3 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors shadow-lg"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default VirtualTryOnModal;
